import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Bug, Trash2, Brain, ChevronDown, ChevronUp, Check,
  Smartphone, Wifi, Battery, MapPin, Clock, Video, List, Code, Share2,
  Target, Pencil, X as XIcon, Users, Download
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { API } from '../utils/constants';
import './TestDetailPage.css';

function getVideoUrl(bug) {
  if (!bug.recording_url) return null;
  if (bug.recording_url.startsWith('http')) return bug.recording_url;
  const base = API.replace(/\/api\/?$/, '');
  return base + bug.recording_url;
}

function safeJsonParse(v) {
  try {
    if (!v) return null;
    if (typeof v === 'string') return JSON.parse(v);
    if (typeof v === 'object') return v;
  } catch { }
  return null;
}

function pick(obj, keys) {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
}

function extractTesterNotes(bug) {
  const raw = (bug.bug_description || bug.description || '').trim();
  if (!raw) return null;

  // 1. If it contains "tester feedback", isolate that part
  const idx = raw.toLowerCase().indexOf('tester feedback');
  let content = raw;
  if (idx !== -1) {
    content = raw.slice(idx).replace(/tester feedback\s*:\s*/i, '').replace(/rating\s*:\s*\d+(\.\d+)?\s*\/\s*\d+/ig, '');
  }

  // 2. Clean up common delimiters and noise
  let cleaned = content
    .replace(/[_═—-]{5,}/g, ' ')  // Long lines of underscores, equals, or dashes
    .replace(/⭐/g, '')             // Star ratings
    .replace(/\s+/g, ' ')          // Collapse whitespace
    .trim();

  // 3. If cleaning result is empty or just generic technical logs, return null
  if (!cleaned) return null;

  const tokens = ['Device:', 'Android:', 'Screen:', 'Battery:', 'Network:', 'Duration:', 'Coordinates:', 'Accuracy:', 'Address:'];
  const hits = tokens.reduce((a, t) => a + (cleaned.includes(t) ? 1 : 0), 0);
  if (hits >= 5) return null;

  return cleaned;
}

function AuthorizedVideo({ url, title }) {
  const [videoSrc, setVideoSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let isMounted = true;
    setLoading(true);
    setError(null);
    setVideoSrc(null);

    async function loadVideo() {
      // Backblaze B2 & other CDN URLs: play directly — no proxy needed
      const needsProxy = url && url.includes('/api/videos');
      if (!needsProxy) {
        if (isMounted) { setVideoSrc(url); setLoading(false); }
        return;
      }
      try {
        const fetchedBlobUrl = await apiClient.getVideoBlobUrl(url);
        objectUrl = fetchedBlobUrl;
        if (isMounted) { setVideoSrc(fetchedBlobUrl); setLoading(false); }
      } catch (err) {
        if (isMounted) { setError(err.message); setLoading(false); }
      }
    }
    if (url) loadVideo();
    else { setLoading(false); }
    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (loading) return <div className="bug-video-placeholder"><div className="vid-spinner" /></div>;
  if (error) return <div className="no-recording-panel"><List size={24} /><span style={{ fontSize: '12px', marginTop: '8px' }}>Load error: {error}</span></div>;
  if (!videoSrc) return <div className="no-recording-panel"><List size={24} /><span style={{ fontSize: '12px', marginTop: '8px' }}>No recording</span></div>;

  return (
    <div className="video-wrapper">
      <video controls preload="metadata" className="bug-video" src={videoSrc} />
    </div>
  );
}

function cleanAIReport(text) {
  if (!text) return '';
  let cleaned = text.split("==== INTERNAL ADMIN VERDICT ====")[0].trim();
  cleaned = cleaned.replace(/^(?:#\s*)?SEVERITY[:\s\n]*(LOW|MEDIUM|HIGH|CRITICAL)[.\s-]*/im, '');
  cleaned = cleaned.replace(/^(as a\s+[\w\s]+tester|in this report|i have audited|hi there|based on my analysis),?\s*/i, '');

  // Standardize headers to standalone H3 for label processing
  cleaned = cleaned.replace(/##\s*BUG REPRODUCTION STEPS/gi, 'Bug reproduction steps\n\n');
  cleaned = cleaned.replace(/BUG REPRODUCTION STEPS[:\s]*/gi, 'Bug reproduction steps\n\n');
  cleaned = cleaned.replace(/Bug reproduction steps[:\s]*/gi, 'Bug reproduction steps\n\n');
  cleaned = cleaned.replace(/##\s*TOP 5 FIXES/gi, 'Top 5 fixes\n\n');
  cleaned = cleaned.replace(/TOP 5 FIXES[:\s]*/gi, 'Top 5 fixes\n\n');

  return cleaned.trim();
}

const CustomDropdown = ({ label, value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  const selectedOpt = options.find(o => o.value === value) || { label: 'Any' };

  return (
    <div className="td-custom-dropdown" ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <label style={{ fontSize: '11px', fontWeight: 500, color: '#6b7280', marginBottom: '6px', display: 'block' }}>{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          background: '#161b22',
          border: `1px solid ${isOpen ? '#58a6ff' : '#30363d'}`,
          borderRadius: '6px',
          padding: '0 12px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#e5e7eb',
          fontSize: '13px',
          cursor: 'pointer',
          outline: 'none',
          transition: 'border-color 0.2s'
        }}
        onMouseEnter={(e) => { if (!isOpen) e.target.style.borderColor = '#484f58'; }}
        onMouseLeave={(e) => { if (!isOpen) e.target.style.borderColor = '#30363d'; }}
      >
        <span>{selectedOpt.label}</span>
        <span style={{ color: '#6b7280', fontSize: '12px' }}>▾</span>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: '#161b22',
          border: '1px solid #30363d',
          borderRadius: '6px',
          overflow: 'hidden',
          zIndex: 100,
          boxShadow: '0 8px 16px rgba(0,0,0,0.5)'
        }}>
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                color: value === opt.value ? '#58a6ff' : '#c9d1d9',
                cursor: 'pointer',
                background: value === opt.value ? 'rgba(56,139,253,0.1)' : 'transparent',
                transition: 'background 0.1s'
              }}
              onMouseEnter={(e) => { if (value !== opt.value) e.target.style.background = '#21262d'; }}
              onMouseLeave={(e) => { if (value !== opt.value) e.target.style.background = 'transparent'; }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BuildTargetingModal = ({ initialData, onClose, onSave }) => {
  const [form, setForm] = useState(initialData);

  const tierOptions = [
    { label: 'Any device', value: '' },
    { label: 'Low-end (budget)', value: 'low' },
    { label: 'Mid-range', value: 'mid' },
    { label: 'Flagship', value: 'high' }
  ];

  const netOptions = [
    { label: 'Any network', value: '' },
    { label: '2G', value: '2g' },
    { label: '3G', value: '3g' },
    { label: '4G / LTE', value: '4g' },
    { label: '5G', value: '5g' },
    { label: 'WiFi only', value: 'wifi' }
  ];

  const inputStyle = {
    background: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '0 12px',
    height: '36px',
    width: '100%',
    color: '#e5e7eb',
    fontSize: '13px',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s'
  };

  const labelStyle = {
    fontSize: '11px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    display: 'block'
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#0d1117', border: '1px solid #21262d', borderRadius: '12px', padding: 0, width: '440px', maxWidth: '90vw', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #21262d', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, color: '#fff', fontSize: '15px', fontWeight: 500 }}>Edit targeting</h2>
          <button
            onClick={onClose}
            style={{ color: '#6b7280', fontSize: '20px', lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none', padding: 0, outline: 'none' }}
            onMouseEnter={e => e.target.style.color = '#e5e7eb'}
            onMouseLeave={e => e.target.style.color = '#6b7280'}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <CustomDropdown
              label="Device Tier"
              value={form.device_tier}
              options={tierOptions}
              onChange={v => setForm(p => ({ ...p, device_tier: v }))}
            />
            <CustomDropdown
              label="Network Type"
              value={form.network_type}
              options={netOptions}
              onChange={v => setForm(p => ({ ...p, network_type: v }))}
            />
          </div>

          <div>
            <label style={labelStyle}>Max RAM (GB)</label>
            <input
              style={inputStyle}
              type="number"
              placeholder="e.g. 4"
              value={form.max_ram_gb}
              onChange={e => setForm(p => ({ ...p, max_ram_gb: e.target.value }))}
              onFocus={e => e.target.style.borderColor = '#58a6ff'}
              onBlur={e => e.target.style.borderColor = '#30363d'}
            />
          </div>

          <div>
            <label style={labelStyle}>Allowed States</label>
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. Maharashtra, Delhi (comma separated)"
              value={form.allowed_states}
              onChange={e => setForm(p => ({ ...p, allowed_states: e.target.value }))}
              onFocus={e => e.target.style.borderColor = '#58a6ff'}
              onBlur={e => e.target.style.borderColor = '#30363d'}
            />
          </div>

          <div>
            <label style={labelStyle}>Allowed Cities</label>
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. Mumbai, Bangalore (comma separated)"
              value={form.allowed_cities}
              onChange={e => setForm(p => ({ ...p, allowed_cities: e.target.value }))}
              onFocus={e => e.target.style.borderColor = '#58a6ff'}
              onBlur={e => e.target.style.borderColor = '#30363d'}
            />
          </div>
        </div>

        <div style={{ padding: '16px 24px 20px', borderTop: '1px solid #21262d', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: '1px solid #30363d', color: '#8b949e', borderRadius: '6px', padding: '6px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', outline: 'none' }}
            onMouseEnter={e => { e.target.style.borderColor = '#484f58'; e.target.style.color = '#c9d1d9'; }}
            onMouseLeave={e => { e.target.style.borderColor = '#30363d'; e.target.style.color = '#8b949e'; }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            style={{ background: 'transparent', border: '1px solid rgba(249,115,22,0.4)', color: '#fb923c', borderRadius: '6px', padding: '6px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', outline: 'none' }}
            onMouseEnter={e => { e.target.style.background = 'rgba(249,115,22,0.1)'; e.target.style.borderColor = 'rgba(249,115,22,0.6)'; }}
            onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.borderColor = 'rgba(249,115,22,0.4)'; }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

function getSuggestedSeverity(bug) {
  const manual = (bug.severity || 'medium').toLowerCase();
  const weights = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
  if (!bug.ai_analysis) return manual;
  const text = bug.ai_analysis.toUpperCase();
  const options = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
  let aiVerdict = null;
  for (const opt of options) {
    if (text.includes(`SEVERITY: ${opt}`) || text.includes(`SEVERITY ${opt}`)) {
      aiVerdict = opt.toLowerCase();
      break;
    }
  }
  if (aiVerdict && weights[aiVerdict] > weights[manual]) return aiVerdict;
  return manual;
}

export default function TestDetailPage({ test, company, onBack, showToast }) {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBug, setExpandedBug] = useState(null);
  const [criteria, setCriteria] = useState(null);
  const [eligibleCount, setEligibleCount] = useState(null);
  const [editingCriteria, setEditingCriteria] = useState(false);
  const [criteriaForm, setCriteriaForm] = useState({
    device_tier: '',
    network_type: '',
    max_ram_gb: '',
    allowed_states: '',
    allowed_cities: ''
  });
  const [shareDuration, setShareDuration] = useState('1d');

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Central loading function
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    const initLoad = async () => {
      // 1. Minimum random delay
      const randomDelay = Math.floor(Math.random() * (1200 - 500 + 1) + 500);
      const delayPromise = new Promise(r => setTimeout(r, randomDelay));

      // 2. Data fetching logic
      const dataTask = async () => {
        // Bug loading
        if (company?.id === 1 || company?.id === '1') {
          setBugs([
            {
              id: 'bug-1',
              title: 'Login screen hangs on slow 4G',
              severity: 'high',
              created_at: new Date(Date.now() - 3600000).toISOString(),
              device: 'Samsung Galaxy S22',
              device_stats: JSON.stringify({ device_model: 'Samsung Galaxy S22', android_version: '13', screen_resolution: '1080x2340', network_type: '4G' }),
              ai_analysis: '# SEVERITY: HIGH\n\nThe application fails to handle network timeouts gracefully on poor 4G connections.\n\n### TOP 5 FIXES\n1. Implement a 15-second timeout.\n2. Add a manual "Retry" button.\n3. Show a "Slow connection" warning.'
            },
            {
              id: 'bug-2',
              title: 'Checkout button unresponsive with emojis',
              severity: 'critical',
              created_at: new Date(Date.now() - 7200000).toISOString(),
              device: 'Pixel 7 Pro',
              device_stats: JSON.stringify({ device_model: 'Pixel 7 Pro', android_version: '14', screen_resolution: '1440x3120', network_type: 'WiFi' }),
              ai_analysis: '# SEVERITY: CRITICAL\n\nThe checkout process breaks when using emojis in input fields.\n\n### TOP 5 FIXES\n1. Update regex validation.\n2. Handle 500 errors gracefully.'
            }
          ]);
          setCriteria({ device_tier: 'High-End', network_type: 'WIFI' });
          setEligibleCount(42);
        } else {
          try {
            const [bugData, critData, testersData] = await Promise.all([
              apiClient.getBugs(test?.id).catch(() => []),
              apiClient.getTestCriteria(test?.id).catch(() => null),
              apiClient.getEligibleTesters(test?.id).catch(() => ({ count: 0 }))
            ]);
            if (!active) return;
            setBugs(Array.isArray(bugData) ? bugData : []);
            setCriteria(critData?.test?.criteria || null);
            setEligibleCount(testersData?.count ?? 0);
          } catch (e) {
            console.error("Fetch Error:", e);
          }
        }
      };

      try {
        await Promise.all([dataTask(), delayPromise]);
      } catch (err) {
        if (active) setError(err.message || 'Error loading');
      } finally {
        if (active) setLoading(false);
      }
    };

    initLoad();
    return () => { active = false; };
  }, [test?.id, company?.id]); // CRITICAL: Only re-run if ID actually changes

  const toggleExpand = (bugId) => setExpandedBug(prev => (prev === bugId ? null : bugId));

  const renderCriteriaChips = () => {
    const c = test?.criteria || criteria || {};
    const chips = [];

    const tierMap = { 'low': 'Low-end', 'mid': 'Mid-range', 'high': 'Flagship' };
    const netMap = { 'wifi': 'WiFi Only', '5g': '5G', '4g': '4G/LTE', '3g': '3G', '2g': '2G' };

    if (c.device_tier && c.device_tier !== 'Unrestricted') {
      const lbl = tierMap[c.device_tier.toLowerCase()] || c.device_tier;
      chips.push({ lbl });
    }
    if (c.network_type) {
      const lbl = netMap[c.network_type.toLowerCase()] || c.network_type;
      chips.push({ lbl });
    }
    if (c.min_ram_gb) {
      chips.push({ lbl: `Min ${c.min_ram_gb}GB RAM` });
    }
    if (c.max_ram_gb) {
      chips.push({ lbl: `Max ${c.max_ram_gb}GB RAM` });
    }
    if (c.allowed_states) {
      chips.push({ lbl: c.allowed_states });
    }
    if (c.allowed_cities) {
      chips.push({ lbl: c.allowed_cities });
    }

    if (chips.length === 0) return <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: 500 }}>Open to all</span>;

    return chips.map((chip, i) => (
      <span key={i} style={{
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid #374151',
        borderRadius: '6px',
        padding: '4px 10px',
        color: '#d1d5db',
        fontSize: '12px',
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'default',
        userSelect: 'none'
      }}>
        {chip.lbl}
      </span>
    ));
  };

  const handleShare = async () => {
    // Determine ID more robustly: direct search in 'test' prop or current state
    let tId = test?.id || test?.idx || test?.test_id;

    // Fallback if 'test' itself is the ID string/number
    if (!tId && (typeof test === 'number' || (typeof test === 'string' && !isNaN(test) && test !== ''))) {
      tId = test;
    }

    if (!tId) {
      showToast('Error: Test ID missing', 'error');
      // For debugging in production/dev
      window.alert(`CRITICAL: ID not found. Test Prop: ${JSON.stringify(test).substring(0, 100)}...`);
      return;
    }

    try {
      console.log(`[DEBUG] Attempting share for tId: ${tId}, duration: ${shareDuration}`);
      const response = await apiClient.generateSharedLink(tId, shareDuration);
      const url = `${window.location.origin}?share=${response.token}`;
      await navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard!');
    } catch (err) {
      console.error('[DEBUG] Share generation failed:', err);
      showToast('Failed to generate link', 'error');
      // Deep debugging alert
      window.alert(`SHARE API FAILED.\nProject ID: ${tId}\nError: ${err.message}\nAction: Check Network tab for /api/tests/${tId}/share status.`);
    }
  };

  const saveCriteria = async (updatedForm) => {
    const dataToUse = updatedForm || criteriaForm;
    try {
      const payload = {
        ...dataToUse,
        max_ram_gb: dataToUse.max_ram_gb ? Number(dataToUse.max_ram_gb) : null
      };
      await apiClient.setTestCriteria(test.id, payload);
      showToast('Criteria updated successfully!');
      setEditingCriteria(false);

      // Refresh local state
      setCriteria(payload);
      setCriteriaForm(payload);

      // Refresh eligible tester count
      try {
        const testersData = await apiClient.getEligibleTesters(test?.id);
        setEligibleCount(testersData?.count ?? 0);
      } catch (e) { console.error("Eligible testers refresh failed:", e); }
    } catch (err) {
      console.error("Save criteria error:", err);
      showToast('Failed to save criteria', 'error');
    }
  };

  const name = test?.app_name || test?.name || 'Project';
  const initials = name.split(' ').filter(x => x).map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'P';

  return (
    <div className="test-detail-page">
      <div className="td-header">
        <button className="td-back-link" onClick={onBack}><ArrowLeft size={16} /> Back to Projects</button>
        <div className="td-header-actions">
          <div className="td-select-wrapper">
            <select value={shareDuration} onChange={(e) => setShareDuration(e.target.value)} className="td-select">
              <option value="1h">1 Hour</option>
              <option value="1d">1 Day</option>
              <option value="7d">7 Days</option>
            </select>
            <ChevronDown size={14} className="td-select-icon" />
          </div>
          <button className="td-share-btn-ghost" onClick={handleShare}><Share2 size={16} /> Share Results</button>
        </div>
      </div>

      <div className={`td-info-hero glass-card ${loading ? 'loading-skeleton' : ''}`}>
        {loading ? (
          <div className="skeleton-hero-content"><div className="skeleton-monogram skeleton"></div><div className="skeleton-hero-text"><div className="skeleton-h1 skeleton"></div><div className="skeleton-meta skeleton"></div></div></div>
        ) : (
          <>
            <div className="td-hero-content">
              <div className="td-app-monogram-large">{initials}</div>
              <div className="td-app-details">
                <h1>{name}</h1>
                <p className="td-meta">
                  <Clock size={14} /> Created {new Date(test?.created_at).toLocaleDateString()}
                  <span className="td-meta-dot">•</span>
                  <Bug size={14} /> {bugs.length} Issues
                  <span className="td-meta-dot">•</span>
                  <span className={`status-pill ${(test?.status || 'pending-approval').toLowerCase().replace(/\s+/g, '-')}`}>
                    {test?.status === 'pending-approval' || !test?.status ? 'Pending' : test.status.charAt(0).toUpperCase() + test.status.slice(1)}
                  </span>
                </p>
              </div>
            </div>
            <div className="td-hero-divider" />

            {test?.instructions && (
              <div className="td-instructions-row" style={{ paddingTop: '16px', paddingBottom: '16px', borderBottom: '1px solid #1f2937' }}>
                <div style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 500, marginBottom: '8px' }}>Instructions</div>
                <div style={{ color: '#d1d5db', fontSize: '13px', lineHeight: 1.5 }}>{test.instructions}</div>
              </div>
            )}

            <div className="td-targeting-section" style={{ paddingTop: '16px' }}>
              <div className="td-targeting-header">
                <div className="td-targeting-label">Targeting</div>
                <div className="td-targeting-actions">
                  <div className="td-eligible-stats">
                    <div className="status-dot-green" />
                    <span>{eligibleCount} eligible testers</span>
                  </div>
                  <button className="td-edit-btn-ghost" onClick={() => {
                    const c = test?.criteria || criteria || {};
                    setCriteriaForm({
                      device_tier: c.device_tier || '',
                      network_type: c.network_type || '',
                      max_ram_gb: c.max_ram_gb || '',
                      allowed_states: c.allowed_states || '',
                      allowed_cities: c.allowed_cities || ''
                    });
                    setEditingCriteria(true);
                  }}>
                    Edit
                  </button>
                </div>
              </div>
              <div className="td-criteria-chips" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{renderCriteriaChips()}</div>
            </div>
          </>
        )}
      </div>

      <div className="td-section-header">{loading ? <div className="skeleton-title skeleton" style={{ width: '180px', height: '24px' }}></div> : <h2 className="td-section-title">Reported Issues <span className="td-count-badge">{bugs.length}</span></h2>}</div>

      <div className="bugs-table">
        <div className="table-header-row-modern"><div className="table-col-header">Title</div><div className="table-col-header">Device</div><div className="table-col-header">Severity</div><div className="table-col-header">Time</div><div className="table-col-header"></div></div>
        {loading ? (
          [1, 2, 3, 4].map(i => <div key={i} className="bug-row-container skeleton" style={{ height: '56px', border: 'none', margin: '8px 0' }} />)
        ) : bugs.length === 0 ? (
          <div className="empty-state-card glass-card"><h3>No reports</h3></div>
        ) : (
          bugs.map((bug) => {
            const isExpanded = expandedBug === bug.id;
            const stats = safeJsonParse(bug.device_stats);
            const videoUrl = getVideoUrl(bug);
            const title = bug.title || 'Untitled';
            const device = bug.tester_device || bug.device || (stats ? pick(stats, ['device_model', 'deviceModel']) : 'Unknown');
            const suggestedSev = getSuggestedSeverity(bug);
            const timeStr = bug.created_at ? (() => {
              const d = new Date(bug.created_at);
              const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
              const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' });
              return `${time} at ${date}`;
            })() : '--:--';
            return (
              <div key={bug.id} className={`bug-row-container-modern ${isExpanded ? 'bug-expanded' : ''}`}>
                <div className="bug-table-row-modern" onClick={() => toggleExpand(bug.id)}>
                  <div className="bug-title-col">{title}</div><div className="bug-device-col-gray">{device}</div><div className="bug-severity-col"><span className={`severity-pill ${suggestedSev}`}>{suggestedSev.toUpperCase()}</span></div><div className="bug-time-col-gray">{timeStr}</div><div className="bug-actions-col"><ChevronDown size={16} className={`chevron-icon ${isExpanded ? 'rotated' : ''}`} /></div>
                </div>
                {isExpanded && (
                  <div className="bug-detail-expand-modern">
                    <div className="bug-detail-grid">
                      <div className="bug-col-left">
                        <div className="section-label-tiny anim-fade-down">Tester notes</div>
                        <div className="bug-notes-bubble anim-fade-down anim-delay-1"><p>{extractTesterNotes(bug) || 'No notes.'}</p></div>
                        <div className="section-label-tiny anim-fade-down anim-delay-2" style={{ marginTop: '24px' }}>AI Report</div>
                        <div className="ai-report-box-modern anim-fade-down anim-delay-3">
                          <div className="ai-severity-row">
                            <span style={{ fontSize: '11px', fontWeight: 500, color: '#9ca3af', textTransform: 'none' }}>Severity</span>
                            {(() => {
                              const s = suggestedSev.toLowerCase();
                              let styles = { background: 'rgba(31,41,55,0.4)', border: '1px solid #374151', color: '#9ca3af' };
                              if (s === 'critical') styles = { background: 'rgba(127,29,29,0.4)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' };
                              if (s === 'high') styles = { background: 'rgba(127,29,29,0.35)', border: '1px solid rgba(220,38,38,0.25)', color: '#fca5a5' };
                              if (s === 'medium') styles = { background: 'rgba(120,53,15,0.3)', border: '1px solid rgba(217,119,6,0.2)', color: '#fde68a' };
                              if (s === 'low') styles = { background: 'rgba(31,41,55,0.4)', border: '1px solid #374151', color: '#9ca3af' };

                              return (
                                <span style={{
                                  marginLeft: '8px',
                                  ...styles,
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  padding: '2px 8px',
                                  borderRadius: '999px',
                                  display: 'inline-block'
                                }}>
                                  {suggestedSev.charAt(0).toUpperCase() + suggestedSev.slice(1)}
                                </span>
                              );
                            })()}
                          </div>
                          <div className="ai-markdown-modern">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({ node, ...props }) => <span style={{ fontSize: '13px', fontWeight: 500, color: '#e5e7eb', marginBottom: '12px', display: 'block' }} {...props} />,
                                h2: ({ node, ...props }) => <span style={{ fontSize: '13px', fontWeight: 500, color: '#e5e7eb', marginBottom: '12px', display: 'block' }} {...props} />,
                                h3: ({ node, ...props }) => <span style={{ fontSize: '13px', fontWeight: 500, color: '#e5e7eb', marginBottom: '12px', display: 'block' }} {...props} />,
                                p: ({ node, ...props }) => <p style={{ color: '#c9d1d9', fontSize: '13px', lineHeight: 1.6, marginBottom: '12px' }} {...props} />,
                                ol: ({ node, children, ...props }) => <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }} {...props}>{children}</div>,
                                ul: ({ node, children, ...props }) => <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }} {...props}>{children}</div>,
                                li: ({ node, children, index, ordered, ...props }) => (
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                    <div style={{ color: '#6b7280', fontSize: '13px', fontWeight: 500, minWidth: '20px', display: 'flex', justifyContent: 'flex-start' }}>
                                      {ordered ? `${index + 1}.` : '•'}
                                    </div>
                                    <div style={{ color: '#c9d1d9', fontSize: '13px', lineHeight: 1.6, flex: 1 }}>
                                      {children}
                                    </div>
                                  </div>
                                )
                              }}
                            >
                              {cleanAIReport(bug.ai_analysis)}
                            </ReactMarkdown>
                          </div>
                        </div>


                      </div>
                      <div className="bug-col-right">
                        <div className="section-label-tiny anim-fade-down anim-delay-1">Screen recording</div>
                        <div className="anim-fade-down anim-delay-2">{videoUrl ? <AuthorizedVideo url={videoUrl} title={title} /> : <div className="no-recording-panel"><List size={24} /></div>}</div>
                        <div className="section-label-tiny anim-fade-down anim-delay-3 standout" style={{ marginTop: '24px' }}>Session specs</div>
                        <div className="specs-grid-modern anim-fade-down anim-delay-4">
                          <div className="spec-item-modern">
                            <div className="spec-label-modern">Device</div>
                            <div className="spec-value-modern">{device}</div>
                          </div>
                          {(bug.tester_os || (stats && pick(stats, ['android_version', 'osVersion', 'androidVersion', 'os_version', 'os', 'release', 'system']))) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">OS</div>
                              <div className="spec-value-modern">{bug.tester_os || pick(stats, ['android_version', 'osVersion', 'androidVersion', 'os_version', 'os', 'release', 'system'])}</div>
                            </div>
                          )}
                          {(bug.tester_net || (stats && pick(stats, ['network_type', 'networkType', 'network', 'connection', 'net']))) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Network</div>
                              <div className="spec-value-modern">{(bug.tester_net || pick(stats, ['network_type', 'networkType', 'network', 'connection', 'net']) || '').toUpperCase()}</div>
                            </div>
                          )}
                          {(bug.tester_ram || (stats && pick(stats, ['ram_gb', 'ram', 'memory', 'totalRam', 'total_ram']))) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">RAM</div>
                              <div className="spec-value-modern">{bug.tester_ram || pick(stats, ['ram_gb', 'ram', 'memory', 'totalRam', 'total_ram'])} GB</div>
                            </div>
                          )}
                          {(bug.tester_res || (stats && pick(stats, ['screen_resolution', 'resolution', 'res']))) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Screen</div>
                              <div className="spec-value-modern">{bug.tester_res || pick(stats, ['screen_resolution', 'resolution', 'res'])}</div>
                            </div>
                          )}
                          {(bug.tester_tier || (stats && pick(stats, ['device_tier', 'tier']))) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Tier</div>
                              <div className="spec-value-modern">{(bug.tester_tier || pick(stats, ['device_tier', 'tier']) || '').toUpperCase()}</div>
                            </div>
                          )}
                          {(bug.tester_city || bug.tester_state) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Location</div>
                              <div className="spec-value-modern">{[bug.tester_city, bug.tester_state].filter(Boolean).join(', ')}</div>
                            </div>
                          )}
                          {(bug.tester_real_name || bug.tester_name) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Tester</div>
                              <div className="spec-value-modern">{bug.tester_real_name || bug.tester_name}</div>
                            </div>
                          )}
                          {(bug.test_duration || bug.duration || (stats && pick(stats, ['duration_seconds', 'duration', 'session_duration', 'duration_sec']))) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Session</div>
                              <div className="spec-value-modern">{bug.test_duration || bug.duration || pick(stats, ['duration_seconds', 'duration', 'session_duration', 'duration_sec'])}s</div>
                            </div>
                          )}
                          {(stats && pick(stats, ['networkSpeed', 'network_speed'])) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Speed</div>
                              <div className="spec-value-modern">{pick(stats, ['networkSpeed', 'network_speed'])}</div>
                            </div>
                          )}
                          {(stats && pick(stats, ['didNetworkDropDuringTest']) !== null) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Net Drop</div>
                              <div className="spec-value-modern" style={{ color: pick(stats, ['didNetworkDropDuringTest']) ? '#ef4444' : 'inherit' }}>{pick(stats, ['didNetworkDropDuringTest']) ? 'Yes ⚠️' : 'No'}</div>
                            </div>
                          )}
                          {(stats && pick(stats, ['highestThermalState'])) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Thermal</div>
                              <div className="spec-value-modern">{pick(stats, ['highestThermalState'])}</div>
                            </div>
                          )}
                          {(stats && pick(stats, ['availableMemoryMb'])) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Free RAM</div>
                              <div className="spec-value-modern">{pick(stats, ['availableMemoryMb'])} MB</div>
                            </div>
                          )}
                          {(stats && pick(stats, ['availableStorageMb'])) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Storage</div>
                              <div className="spec-value-modern">{pick(stats, ['availableStorageMb'])} MB</div>
                            </div>
                          )}
                          {(stats && pick(stats, ['isPowerSaveMode']) !== null) && (
                            <div className="spec-item-modern">
                              <div className="spec-label-modern">Battery Saver</div>
                              <div className="spec-value-modern">{pick(stats, ['isPowerSaveMode']) ? 'On' : 'Off'}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {editingCriteria && (
        <BuildTargetingModal
          initialData={criteriaForm}
          onClose={() => setEditingCriteria(false)}
          onSave={async (updatedForm) => {
            await saveCriteria(updatedForm);
          }}
        />
      )}
    </div>
  );
}